import { Link, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { useUser } from '../context/UserContext'
import {
  canAccessGestionCampana,
  canAccessMarketingMenu,
  canAccessNoOperados,
  canAccessSeguimientoEjecutivo,
  canAccessControlGestionMenu,
  canAccessClasificacionRuts,
  canAccessVentaAnual,
  canAccessConsultaOtorgamientos,
  canAccessAnalisisPrecios,
  canAccessSeguimientoCesionesFacturacion,
  canAccessPanelComercial,
  canAccessSolicitudesExcepcionMora,
  canAccessSimuladorMora,
  canAccessTasasMoraPreacordadas,
  canAccessCargaMasivaLeads,
  canAccessRecapturaMenu,
  canAccessRecapturaOperativo,
  canAccessRecapturaResumen,
  canDecideRecaptura,
  canAccessRecapturaCarga,
  isAplicacionesUser,
  isComercialUser,
} from '../utils/gestionCampanaAccess'
import { canUsePermissionDebug } from '../utils/permissionDebug'
import { 
  MdHome, 
  MdCreditCard, 
  MdPeople, 
  MdList, 
  MdCampaign, 
  MdBuild, 
  MdCalendarToday, 
  MdGroup,
  MdChevronRight,
  MdGroupAdd,
  MdPerson,
  MdPersonAddAlt1,
  MdAccountTree,
} from 'react-icons/md'

const Sidebar = ({ open = true }) => {
  const location = useLocation()
  const { user, realUser } = useUser()
  // realUser puede faltar tras HMR del contexto; caer al user de sesión.
  const showPermissionDebug = canUsePermissionDebug(realUser || user)
  const soloComercial = isComercialUser(user)
  const menuAmpliado = !isAplicacionesUser(user) && !soloComercial
  const [expandedMenus, setExpandedMenus] = useState(
    () => (isAplicacionesUser(user) || isComercialUser(user) ? new Set(['herramientas']) : new Set())
  )

  const toggleMenu = (menuName) => {
    setExpandedMenus(prev => {
      const newSet = new Set(prev)
      if (newSet.has(menuName)) {
        newSet.delete(menuName)
      } else {
        newSet.add(menuName)
      }
      return newSet
    })
  }

  return (
    <aside
      className={`bg-slate-700 text-white w-64 h-[calc(100vh-3rem)] fixed left-0 top-12 z-40 overflow-y-auto transition-transform duration-200 ease-out ${
        open ? 'translate-x-0 pointer-events-auto' : '-translate-x-full pointer-events-none'
      }`}
      aria-hidden={!open}
    >
      {/* User Profile Section */}
      <div className="p-4" style={{backgroundColor: '#ec281c'}}>
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
            <MdPerson className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <div className="font-semibold text-white">{user?.nombre || 'Usuario'}</div>
            <div className="text-teal-200 text-sm">{user?.tipo_usuario || 'Sin rol'}</div>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="p-4">
        
        <ul className="space-y-1">

          {menuAmpliado && (
          <li>
            <Link 
              to="/inicio" 
              className={`flex items-center px-3 py-2 rounded text-sm hover:bg-slate-600 transition-colors ${
                location.pathname === '/inicio' ? 'bg-slate-600 text-white' : 'text-gray-300'
              }`}
            >
              <MdHome className="w-4 h-4 mr-3" />
              <span>Inicio</span>
            </Link>
          </li>
          )}

          {menuAmpliado && (
          <>
          <li>
            <button 
              onClick={() => toggleMenu('cartera')}
              className="w-full flex items-center px-3 py-2 rounded text-sm text-gray-300 hover:bg-slate-600 transition-colors text-left"
            >
              <MdCreditCard className="w-4 h-4 mr-3" />
              <span>Cartera Sistema Factoring</span>
              <MdChevronRight className={`w-4 h-4 ml-auto transition-transform ${expandedMenus.has('cartera') ? 'rotate-90' : ''}`} />
            </button>
            {expandedMenus.has('cartera') && (
              <ul className="ml-10 mt-2 space-y-1">
                <li>
                  <Link 
                    to="/cartera-ejecutivo" 
                    className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    Cartera
                  </Link>
                </li>
                <li>
                  <Link to="/ingresar-tubo-negocio" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                    Tubo de negocio
                  </Link>
                </li>
                <li>
                  <Link to="/bolsa-documentos" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                    Bolsa de documentos
                  </Link>
                </li>
                <li>
                  <Link to="/campanas" className="flex items-center px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                    <span>Campañas</span>
                    <MdCampaign className="w-4 h-4 ml-auto" />
                  </Link>
                </li>
              </ul>
            )}
          </li>

          {menuAmpliado && canAccessRecapturaMenu(user) && (
          <li>
            <button
              onClick={() => toggleMenu('recaptura')}
              className={`w-full flex items-center px-3 py-2 rounded text-sm hover:bg-slate-600 transition-colors text-left ${
                location.pathname.startsWith('/recaptura') ? 'bg-slate-600 text-white' : 'text-gray-300'
              }`}
            >
              <MdCampaign className="w-4 h-4 mr-3" />
              <span>Activación Comercial</span>
              <MdChevronRight className={`w-4 h-4 ml-auto transition-transform ${expandedMenus.has('recaptura') ? 'rotate-90' : ''}`} />
            </button>
            {expandedMenus.has('recaptura') && (
              <ul className="ml-10 mt-2 space-y-1">
                {canAccessRecapturaOperativo(user) && (
                  <>
                    <li>
                      <Link to="/recaptura/matriz" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                        Matriz comercial
                      </Link>
                    </li>
                    <li>
                      <Link to="/recaptura/bitacora" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                        Bitácora
                      </Link>
                    </li>
                  </>
                )}
                {canAccessRecapturaResumen(user) && (
                  <li>
                    <Link to="/recaptura/resumen" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                      Resumen gerencial
                    </Link>
                  </li>
                )}
                {canDecideRecaptura(user) && (
                  <li>
                    <Link to="/recaptura/aprobaciones" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                      Aprobaciones
                    </Link>
                  </li>
                )}
                {canAccessRecapturaCarga(user) && (
                  <li>
                    <Link to="/recaptura/carga" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                      Carga Excel
                    </Link>
                  </li>
                )}
              </ul>
            )}
          </li>
          )}
          
          <li>
            <button 
              onClick={() => toggleMenu('potenciales')}
              className="w-full flex items-center px-3 py-2 rounded text-sm text-gray-300 hover:bg-slate-600 transition-colors text-left"
            >
              <MdPeople className="w-4 h-4 mr-3" />
              <span>Potenciales Clientes</span>
              <MdChevronRight className={`w-4 h-4 ml-auto transition-transform ${expandedMenus.has('potenciales') ? 'rotate-90' : ''}`} />
            </button>
            {expandedMenus.has('potenciales') && (
              <ul className="ml-10 mt-2 space-y-1">
                <li>
                  <Link to="/en-gestion" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                    En gestión
                  </Link>
                </li>
                <li>
                  <Link to="/perdidos" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                    Perdidos
                  </Link>
                </li>
                <li>
                  <Link to="/ingresar-registro" className="flex items-center px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                    <span>Ingresar registro</span>
                    <MdPersonAddAlt1 className="w-4 h-4 ml-auto" />
                  </Link>
                </li>
                {canAccessCargaMasivaLeads(user) && (
                  <li>
                    <Link to="/carga-masiva-leads" className="flex items-center px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                      <span>Carga masiva</span>
                      <MdGroupAdd className="w-4 h-4 ml-auto" />
                    </Link>
                  </li>
                )}
              </ul>
            )}
          </li>
          
          <li>
            <button 
              onClick={() => toggleMenu('seguimiento')}
              className="w-full flex items-center px-3 py-2 rounded text-sm text-gray-300 hover:bg-slate-600 transition-colors text-left"
            >
              <MdList className="w-4 h-4 mr-3" />
              <span>Seguimiento comercial</span>
              <MdChevronRight className={`w-4 h-4 ml-auto transition-transform ${expandedMenus.has('seguimiento') ? 'rotate-90' : ''}`} />
            </button>
            {expandedMenus.has('seguimiento') && (
              <ul className="ml-10 mt-2 space-y-1">
                <li>
                  <Link to="/factoring" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                    Factoring
                  </Link>
                </li>
                <li>
                  <Link to="/credito" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                    Crédito
                  </Link>
                </li>
                <li>
                  <Link to="/leasing" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                    Leasing
                  </Link>
                </li>
              </ul>
            )}
          </li>
          </>
          )}

          <li>
            <button 
              onClick={() => toggleMenu('herramientas')}
              className="w-full flex items-center px-3 py-2 rounded text-sm text-gray-300 hover:bg-slate-600 transition-colors text-left"
            >
              <MdBuild className="w-4 h-4 mr-3" />
              <span>Caja de herramientas</span>
              <MdChevronRight className={`w-4 h-4 ml-auto transition-transform ${expandedMenus.has('herramientas') ? 'rotate-90' : ''}`} />
            </button>
            {expandedMenus.has('herramientas') && (
              <ul className="ml-10 mt-2 space-y-1">
                {menuAmpliado && (
                  <>
                <li>
                  <Link to="/simulador-operaciones" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                    Simulador de operaciones
                  </Link>
                </li>
                <li>
                  <Link to="/listado-simulacion" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                    Listado de simulaciones
                  </Link>
                </li>
                <li>
                  <Link to="/buscador-clientes" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                    Buscador de Clientes
                  </Link>
                </li>
                {canAccessSimuladorMora(user) && (
                <li>
                  <Link
                    to="/simulador-mora-excepcion"
                    className={`block px-3 py-1 text-sm transition-colors ${
                      location.pathname.startsWith('/simulador-mora-excepcion')
                        ? 'text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Simulador Mora y Excepción
                  </Link>
                </li>
                )}
                  </>
                )}
                {canAccessSolicitudesExcepcionMora(user) && !soloComercial && (
                  <li>
                    <Link
                      to="/solicitudes-excepcion-mora"
                      className={`block px-3 py-1 text-sm transition-colors ${
                        location.pathname.startsWith('/solicitudes-excepcion-mora')
                          ? 'text-white'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Solicitudes excepción mora
                    </Link>
                  </li>
                )}
                {canAccessTasasMoraPreacordadas(user) && !soloComercial && (
                  <li>
                    <Link
                      to="/tasas-mora-preacordadas"
                      className={`block px-3 py-1 text-sm transition-colors ${
                        location.pathname.startsWith('/tasas-mora-preacordadas')
                          ? 'text-white'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Tasas mora preacordadas
                    </Link>
                  </li>
                )}
                {menuAmpliado && canAccessConsultaOtorgamientos(user) && (
                  <li>
                    <Link to="/consulta-otorgamientos" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                      Consulta otorgamientos
                    </Link>
                  </li>
                )}
                {menuAmpliado && canAccessAnalisisPrecios(user) && (
                  <li>
                    <Link
                      to="/analisis-precios"
                      className={`block px-3 py-1 text-sm transition-colors ${
                        location.pathname.startsWith('/analisis-precios')
                          ? 'text-white'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Análisis de precios
                    </Link>
                  </li>
                )}
                {menuAmpliado && canAccessSeguimientoCesionesFacturacion(user) && (
                  <li>
                    <Link
                      to="/seguimiento-cesiones-facturacion"
                      className={`block px-3 py-1 text-sm transition-colors ${
                        location.pathname.startsWith('/seguimiento-cesiones-facturacion')
                          ? 'text-white'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Seguimiento cesiones/facturación
                    </Link>
                  </li>
                )}
                {canAccessPanelComercial(user) && (
                  <li>
                    <Link
                      to="/panel-comercial"
                      className={`block px-3 py-1 text-sm transition-colors ${
                        location.pathname.startsWith('/panel-comercial')
                          ? 'text-white'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Panel Comercial
                    </Link>
                  </li>
                )}
                {canAccessPanelComercial(user) && (
                  <li>
                    <Link
                      to="/panel-gerencial"
                      className={`block px-3 py-1 text-sm transition-colors ${
                        location.pathname.startsWith('/panel-gerencial')
                          ? 'text-white'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      Panel Gerencial{' '}
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-300">
                        (En Construcción)
                      </span>
                    </Link>
                  </li>
                )}
              </ul>
            )}
          </li>
          
          {menuAmpliado && canAccessMarketingMenu(user) && (
            <li>
              <button
                onClick={() => toggleMenu('gestion-campana')}
                className={`w-full flex items-center px-3 py-2 rounded text-sm hover:bg-slate-600 transition-colors text-left ${
                  location.pathname.startsWith('/panel-marketing') ? 'bg-slate-600 text-white' : 'text-gray-300'
                }`}
              >
                <MdList className="w-4 h-4 mr-3" />
                <span>Gestión Campaña</span>
                <MdChevronRight
                  className={`w-4 h-4 ml-auto transition-transform ${
                    expandedMenus.has('gestion-campana') ? 'rotate-90' : ''
                  }`}
                />
              </button>
              {expandedMenus.has('gestion-campana') && (
                <ul className="ml-10 mt-2 space-y-1">
                  {canAccessGestionCampana(user) && (
                    <>
                      <li>
                        <Link
                          to="/panel-marketing/placeholder-1"
                          className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors"
                        >
                          Detalle Otorgamientos
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/panel-marketing/carga-campana"
                          className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors"
                        >
                          Carga Campaña
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/panel-marketing/carga-meta-volumen"
                          className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors"
                        >
                          Carga Meta Volumen
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/panel-marketing/mantenedor-subgerentes"
                          className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors"
                        >
                          Mantenedor subgerente
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/panel-marketing/connect-tarjetas"
                          className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors"
                        >
                          Connect — Tarjetas
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/panel-marketing/connect-registros"
                          className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors"
                        >
                          Connect — Registros
                        </Link>
                      </li>
                    </>
                  )}
                  {canAccessSeguimientoEjecutivo(user) && (
                    <li>
                      <Link
                        to="/panel-marketing/seguimiento-ejecutivo"
                        className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors"
                      >
                        Seguimiento ejecutivos
                      </Link>
                    </li>
                  )}
                  {canAccessNoOperados(user) && (
                  <li>
                    <Link
                      to="/panel-marketing/no-operados"
                      className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      Clientes No Operados
                    </Link>
                  </li>
                  )}
                </ul>
              )}
            </li>
          )}

          {menuAmpliado && canAccessControlGestionMenu(user) && (
            <li>
              <button
                onClick={() => toggleMenu('control-gestion')}
                className={`w-full flex items-center px-3 py-2 rounded text-sm hover:bg-slate-600 transition-colors text-left ${
                  location.pathname.startsWith('/control-gestion') ||
                  location.pathname.startsWith('/clasificacion-ruts')
                    ? 'bg-slate-600 text-white'
                    : 'text-gray-300'
                }`}
              >
                <MdAccountTree className="w-4 h-4 mr-3" />
                <span>Control de gestión</span>
                <MdChevronRight
                  className={`w-4 h-4 ml-auto transition-transform ${
                    expandedMenus.has('control-gestion') ? 'rotate-90' : ''
                  }`}
                />
              </button>
              {expandedMenus.has('control-gestion') && (
                <ul className="ml-10 mt-2 space-y-1">
                  {canAccessClasificacionRuts(user) && (
                    <li>
                      <Link
                        to="/control-gestion/clasificacion-ruts"
                        className={`block px-3 py-1 text-sm transition-colors ${
                          location.pathname.startsWith('/control-gestion/clasificacion-ruts') ||
                          location.pathname.startsWith('/clasificacion-ruts')
                            ? 'text-white'
                            : 'text-gray-400 hover:text-white'
                        }`}
                      >
                        Clasificación RUTs
                      </Link>
                    </li>
                  )}
                  {canAccessVentaAnual(user) && (
                    <li>
                      <Link
                        to="/control-gestion/venta-anual"
                        className={`block px-3 py-1 text-sm transition-colors ${
                          location.pathname.startsWith('/control-gestion/venta-anual')
                            ? 'text-white'
                            : 'text-gray-400 hover:text-white'
                        }`}
                      >
                        Venta anual
                      </Link>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}

          {user?.tipo_usuario === 'Administrador' && (
            <li>
              <button 
                onClick={() => toggleMenu('generar')}
                className="w-full flex items-center px-3 py-2 rounded text-sm text-gray-300 hover:bg-slate-600 transition-colors text-left"
              >
                <MdCalendarToday className="w-4 h-4 mr-3" />
                <span>Generar Seguimiento</span>
                <MdChevronRight className={`w-4 h-4 ml-auto transition-transform ${expandedMenus.has('generar') ? 'rotate-90' : ''}`} />
              </button>
              {expandedMenus.has('generar') && (
                <ul className="ml-10 mt-2 space-y-1">
                  <li>
                    <Link to="/crear-reporte-diario" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                      Crear reporte diario
                    </Link>
                  </li>
                  <li>
                    <Link to="/ingresar-devoluciones-manuales" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                      Ingresar devoluciones manuales
                    </Link>
                  </li>
                  <li>
                    <Link to="/ingresar-metas" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                      Ingresar metas
                    </Link>
                  </li>
                </ul>
              )}
            </li>
          )}
          
          {user?.tipo_usuario === 'Administrador' && (
            <li>
              <button 
                onClick={() => toggleMenu('usuarios')}
                className="w-full flex items-center px-3 py-2 rounded text-sm text-gray-300 hover:bg-slate-600 transition-colors text-left"
              >
                <MdGroupAdd className="w-4 h-4 mr-3" />
                <span>Usuarios</span>
                <MdChevronRight className={`w-4 h-4 ml-auto transition-transform ${expandedMenus.has('usuarios') ? 'rotate-90' : ''}`} />
              </button>
              {expandedMenus.has('usuarios') && (
                <ul className="ml-10 mt-2 space-y-1">
                  <li>
                    <Link to="/usuarios/gestion" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                      Gestión de Usuarios
                    </Link>
                  </li>
                  <li>
                    <Link to="/usuarios/administracion-metas" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                      Administración de metas
                    </Link>
                  </li>
                  <li>
                    <Link to="/usuarios/meta-presupuesto-gerencia" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                      Meta presupuesto Gerencia
                    </Link>
                  </li>
                  <li>
                    <Link to="/usuarios/sync-ejec-hist" className="block px-3 py-1 text-sm text-gray-400 hover:text-white transition-colors">
                      Sync notificaciones facturas
                    </Link>
                  </li>
                </ul>
              )}
            </li>
          )}

          {showPermissionDebug && (
            <li className="pt-3 mt-3 border-t border-slate-600">
              <Link
                to="/debug-permisos"
                className={`flex items-center px-3 py-2 rounded text-sm hover:bg-slate-600 transition-colors ${
                  location.pathname.startsWith('/debug-permisos')
                    ? 'bg-amber-700/40 text-amber-100'
                    : 'text-amber-200'
                }`}
              >
                <MdBuild className="w-4 h-4 mr-3" />
                <span>Debug permisos</span>
              </Link>
            </li>
          )}
        </ul>
      </nav>
    </aside>
  )
}

export default Sidebar